import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { motion, AnimatePresence } from "motion/react";
import { FiX } from "react-icons/fi";
import { HiSparkles } from "react-icons/hi2";
import { TbCookie } from "react-icons/tb";

const CookieConsent = () => {
    const [mounted, setMounted] = useState(false);
    const [visible, setVisible] = useState(false);

    useEffect(() => setMounted(true), []);

    useEffect(() => {
        const consent = localStorage.getItem("cookieConsent");
        if (!consent) {
            const t = setTimeout(() => setVisible(true), 600);
            return () => clearTimeout(t);
        }
    }, []);

    const handleAccept = () => {
        localStorage.setItem("cookieConsent", "accepted");
        setVisible(false);
    };

    const handleReject = () => {
        localStorage.setItem("cookieConsent", "rejected");
        setVisible(false);
    };

    if (!mounted) return null;

    const content = (
        <AnimatePresence>
            {visible && (
                <motion.div
                    initial={{ y: 80, opacity: 0, scale: 0.95 }}
                    animate={{ y: 0, opacity: 1, scale: 1 }}
                    exit={{ y: 80, opacity: 0, scale: 0.95 }}
                    transition={{ type: "spring", stiffness: 240, damping: 26 }}
                    className='fixed bottom-3 left-3 right-3 md:bottom-5 md:left-5 md:right-auto md:max-w-md z-[60]'
                >
                    <div
                        className='relative overflow-hidden rounded-2xl md:rounded-3xl p-4 md:p-5'
                        style={{
                            background:
                                "linear-gradient(165deg, rgba(28,18,48,0.92), rgba(10,8,16,0.96))",
                            backdropFilter: "blur(24px) saturate(180%)",
                            WebkitBackdropFilter: "blur(24px) saturate(180%)",
                            border: "1px solid rgba(255,255,255,0.14)",
                            boxShadow:
                                "0 24px 60px -16px rgba(127,0,255,0.45), 0 12px 28px -10px rgba(0,0,0,0.7), inset 0 1px 0 rgba(255,255,255,0.16)",
                        }}
                    >
                        {/* Decorative orbs — static (animated blur is a heavy per-frame repaint) */}
                        <div
                            aria-hidden
                            className='absolute -top-10 -right-10 w-40 h-40 rounded-full opacity-35 blur-3xl pointer-events-none'
                            style={{
                                background:
                                    "radial-gradient(circle, #7F00FF 0%, transparent 70%)",
                            }}
                        />
                        <div
                            aria-hidden
                            className='absolute -bottom-12 -left-12 w-40 h-40 rounded-full opacity-25 blur-3xl pointer-events-none'
                            style={{
                                background:
                                    "radial-gradient(circle, #FCFA02 0%, transparent 70%)",
                            }}
                        />

                        {/* Close (Reject) */}
                        <motion.button
                            onClick={handleReject}
                            whileHover={{ scale: 1.1, rotate: 90 }}
                            whileTap={{ scale: 0.9 }}
                            aria-label='Close'
                            className='absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center text-white/70 hover:text-white z-10'
                            style={{
                                background: "rgba(255,255,255,0.06)",
                                border: "1px solid rgba(255,255,255,0.12)",
                            }}
                        >
                            <FiX className='text-sm' />
                        </motion.button>

                        <div className='relative flex items-start gap-3 md:gap-4'>
                            {/* Cookie icon */}
                            <motion.div
                                initial={{ scale: 0, rotate: -90 }}
                                animate={{ scale: 1, rotate: 0 }}
                                transition={{
                                    type: "spring",
                                    stiffness: 220,
                                    damping: 16,
                                    delay: 0.15,
                                }}
                                className='flex-shrink-0 w-11 h-11 md:w-12 md:h-12 rounded-2xl flex items-center justify-center relative overflow-hidden'
                                style={{
                                    background:
                                        "linear-gradient(135deg, #FCFA02, #FF8AFF, #7F00FF)",
                                    boxShadow:
                                        "0 8px 22px -6px rgba(127,0,255,0.55), inset 0 1px 0 rgba(255,255,255,0.3)",
                                }}
                            >
                                <motion.div
                                    animate={{ rotate: [0, 8, -8, 0] }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                    }}
                                >
                                    <TbCookie className='text-2xl text-white drop-shadow-md' />
                                </motion.div>
                                {/* Sparkle */}
                                <motion.div
                                    animate={{
                                        scale: [0.6, 1.2, 0.6],
                                        opacity: [0.4, 1, 0.4],
                                    }}
                                    transition={{
                                        duration: 1.8,
                                        repeat: Infinity,
                                        ease: "easeInOut",
                                    }}
                                    className='absolute top-1 right-1'
                                >
                                    <HiSparkles
                                        className='text-[10px]'
                                        style={{ color: "#fff" }}
                                    />
                                </motion.div>
                            </motion.div>

                            {/* Text */}
                            <div className='flex-1 min-w-0 pr-6'>
                                <h4 className='text-client-font font-bold text-sm md:text-base tracking-tight mb-1'>
                                    We use cookies
                                </h4>
                                <p className='text-client-font/65 text-[11px] md:text-xs leading-relaxed'>
                                    To improve your experience, personalize content, and
                                    analyze traffic. By clicking{" "}
                                    <span
                                        className='font-semibold'
                                        style={{ color: "#FCFA02" }}
                                    >
                                        Accept
                                    </span>
                                    , you consent to our use of cookies.
                                </p>
                            </div>
                        </div>

                        {/* Buttons */}
                        <div className='relative mt-4 flex items-center gap-2'>
                            <motion.button
                                onClick={handleReject}
                                whileHover={{ scale: 1.03, y: -1 }}
                                whileTap={{ scale: 0.97 }}
                                className='flex-1 py-2 md:py-2.5 rounded-xl text-client-font/80 text-xs md:text-sm font-medium'
                                style={{
                                    background: "rgba(255,255,255,0.05)",
                                    border: "1px solid rgba(255,255,255,0.12)",
                                    backdropFilter: "blur(10px)",
                                }}
                            >
                                Reject
                            </motion.button>
                            <motion.button
                                onClick={handleAccept}
                                whileHover={{ scale: 1.03, y: -1 }}
                                whileTap={{ scale: 0.97 }}
                                className='relative flex-1 py-2 md:py-2.5 rounded-xl text-white text-xs md:text-sm font-semibold overflow-hidden group'
                                style={{
                                    background:
                                        "linear-gradient(135deg, #7F00FF, #FF8AFF)",
                                    boxShadow:
                                        "0 8px 22px -6px rgba(127,0,255,0.6), inset 0 1px 0 rgba(255,255,255,0.25)",
                                }}
                            >
                                <span className='relative z-10'>Accept All</span>
                                <span
                                    aria-hidden
                                    className='absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300'
                                    style={{
                                        background:
                                            "linear-gradient(135deg, #FCFA02, #FF8AFF, #7F00FF)",
                                    }}
                                />
                                <span className='relative z-10'></span>
                            </motion.button>
                        </div>
                    </div>
                </motion.div>
            )}
        </AnimatePresence>
    );

    return createPortal(content, document.body);
};

export default CookieConsent;
